import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import {Button} from '../../../components/ui/Button';
import {Input} from '../../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { formatCurrency } from '../../../utils/localization';
import apiService from '../../../services/api';

const AddExpenseModal = ({ isOpen, onClose, participants, onAddExpense, editingExpense = null, eventId }) => {
  const [splitType, setSplitType] = useState(editingExpense?.splitType || 'equal');
  const [selectedParticipants, setSelectedParticipants] = useState(
    editingExpense?.splitWith?.map(p => p?.id) || participants?.map(p => p?.id)
  );
  const [customSplits, setCustomSplits] = useState(
    editingExpense?.customSplits || {}
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: {
      description: editingExpense?.description || '',
      amount: editingExpense?.amount || '',
      paidBy: editingExpense?.paidBy?.id || participants?.[0]?.id || '',
      category: editingExpense?.category || '',
      date: editingExpense?.date || new Date()?.toISOString()?.split('T')?.[0]
    }
  });

  // Update form values when editingExpense changes
  useEffect(() => {
    if (editingExpense) {
      setValue('description', editingExpense.description || '');
      setValue('amount', editingExpense.amount || '');
      setValue('paidBy', editingExpense.paidBy?.id || participants?.[0]?.id || '');
      setValue('category', editingExpense.category || '');
      setValue('date', editingExpense.date || new Date().toISOString().split('T')[0]);
      
      // Update local state as well
      setSplitType(editingExpense.splitType || 'equal');
      setSelectedParticipants(editingExpense.splitWith?.map(p => p?.id) || participants?.map(p => p?.id));
      setCustomSplits(editingExpense.customSplits || {});
    } else {
      // Reset to default values when not in editing mode
      setValue('description', '');
      setValue('amount', '');
      setValue('paidBy', participants?.[0]?.id || '');
      setValue('category', '');
      setValue('date', new Date().toISOString().split('T')[0]);
      
      setSplitType('equal');
      setSelectedParticipants(participants?.map(p => p?.id));
      setCustomSplits({});
    }
  }, [editingExpense, participants, setValue]);

  const watchedAmount = watch('amount');

  const participantOptions = participants?.map(p => ({
    value: p?.id,
    label: p?.name
  }));

  const categoryOptions = [
    { value: 'food', label: 'Food & Dining' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'shopping', label: 'Shopping' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'other', label: 'Other' }
  ];

  const handleParticipantToggle = (participantId) => {
    setSelectedParticipants(prev => {
      const newSelection = prev?.includes(participantId)
        ? prev?.filter(id => id !== participantId)
        : [...prev, participantId];
      
      // Reset custom splits when participants change
      if (splitType === 'custom') {
        const newCustomSplits = {};
        newSelection?.forEach(id => {
          newCustomSplits[id] = customSplits?.[id] || 0;
        });
        setCustomSplits(newCustomSplits);
      }
      
      return newSelection;
    });
  };

  const handleCustomSplitChange = (participantId, amount) => {
    setCustomSplits(prev => ({
      ...prev,
      [participantId]: parseFloat(amount) || 0
    }));
  };

  const getTotalCustomSplit = () => {
    return Object.values(customSplits)?.reduce((sum, amount) => sum + (amount || 0), 0);
  };

  const onSubmit = async (data) => {
    try {
      // Prepare expense data
      const expenseData = {
        description: data.description,
        amount: parseFloat(data.amount),
        category: data.category,
        date: data.date,
        splitType: splitType,
        paidBy: data.paidBy,
        participantIds: selectedParticipants, // Use participantIds as expected by the API
      };

      // Only add customSplits if splitType is 'custom'
      if (splitType === 'custom') {
        expenseData.customSplits = customSplits;
      }

      if (editingExpense) {
        // Update existing expense via API
        await apiService.updateExpense(editingExpense.id, expenseData);
      } else {
        // Create new expense via API
        await apiService.createExpense(eventId, expenseData);
      }

      // Refresh the event details after successful API call
      onAddExpense(expenseData);
      handleClose();
    } catch (error) {
      console.error('Error adding expense:', error);
      alert(error.message || 'Failed to save expense');
    }
  };

  const handleClose = () => {
    reset();
    setSplitType('equal');
    setSelectedParticipants(participants?.map(p => p?.id));
    setCustomSplits({});
    onClose();
  };

   if (!isOpen) return null;

  return (
    <div className="mt-10 fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {editingExpense ? 'Edit Expense' : 'Add New Expense'}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Description"
              placeholder="What was this expense for?"
              error={errors?.description?.message}
              required
              {...register('description', {
                required: 'Description is required',
                minLength: { value: 2, message: 'Description must be at least 2 characters' }
              })}
            />
            
            <Input
              label="Amount"
              type="number"
              step="0.01"
              placeholder="0.00"
              error={errors?.amount?.message}
              required
              {...register('amount', {
                required: 'Amount is required',
                min: { value: 0.01, message: 'Amount must be greater than 0' }
              })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="paidBy"
              control={control}
              rules={{ required: 'Please select who paid' }}
              render={({ field }) => (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Paid by <span className="text-destructive">*</span>
                  </label>
                  <Select onValueChange={field.onChange} value={field.value} {...field}>
                    <SelectTrigger className={errors?.paidBy?.message ? "border-destructive" : ""}>
                      <SelectValue placeholder="Select who paid" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {participantOptions && participantOptions.length > 0 ? (
                        participantOptions?.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="" disabled>No participants available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {errors?.paidBy?.message && (
                    <p className="text-destructive text-sm mt-1">{errors.paidBy.message}</p>
                  )}
                </div>
              )}
            />
            
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Category
                  </label>
                  <Select onValueChange={field.onChange} value={field.value} {...field}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {categoryOptions && categoryOptions.length > 0 ? (
                        categoryOptions?.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="" disabled>No categories available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
          </div>

          <Input
            label="Date"
            type="date"
            error={errors?.date?.message}
            required
            {...register('date', { required: 'Date is required' })}
          />

          {/* Split Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Split Configuration</h3>
            
            {/* Split Type Selection */}
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="equal"
                  checked={splitType === 'equal'}
                  onChange={(e) => setSplitType(e?.target?.value)}
                  className="w-4 h-4 text-primary"
                />
                <span className="text-sm text-foreground">Split equally</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="custom"
                  checked={splitType === 'custom'}
                  onChange={(e) => setSplitType(e?.target?.value)}
                  className="w-4 h-4 text-primary"
                />
                <span className="text-sm text-foreground">Custom split</span>
              </label>
            </div>

            {/* Participant Selection */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground">Split between:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {participants?.map((participant) => (
                  <div key={participant?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedParticipants?.includes(participant?.id)}
                        onChange={() => handleParticipantToggle(participant?.id)}
                      />
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-primary-foreground">
                            {participant?.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm text-foreground">{participant?.name}</span>
                      </div>
                    </div>
                    
                    {splitType === 'custom' && selectedParticipants?.includes(participant?.id) && (
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={customSplits?.[participant?.id] || ''}
                        onChange={(e) => handleCustomSplitChange(participant?.id, e?.target?.value)}
                        className="w-20"
                      />
                    )}
                    
                    {splitType === 'equal' && selectedParticipants?.includes(participant?.id) && watchedAmount && (
                      <span className="text-sm text-muted-foreground">
                        {formatCurrency(parseFloat(watchedAmount) / selectedParticipants?.length)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Split Validation */}
            {splitType === 'custom' && watchedAmount && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total split amount:</span>
                  <span className={`font-medium ${
                    Math.abs(getTotalCustomSplit() - parseFloat(watchedAmount)) < 0.01
                      ? 'text-success' :'text-destructive'
                  }`}>
                    {formatCurrency(getTotalCustomSplit())} / {formatCurrency(parseFloat(watchedAmount))}
                  </span>
                </div>
                {Math.abs(getTotalCustomSplit() - parseFloat(watchedAmount)) >= 0.01 && (
                  <p className="text-xs text-destructive mt-1">
                    Split amounts must equal the total expense amount
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={
                selectedParticipants?.length === 0 ||
                (splitType === 'custom' && watchedAmount && Math.abs(getTotalCustomSplit() - parseFloat(watchedAmount)) >= 0.01)
              }
              className="flex-1"
            >
              {editingExpense ? 'Update Expense' : 'Add Expense'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddExpenseModal;
